// packages/main/app/web/components-v2/PixabayImageBrowser/index.tsx
import { css } from 'aphrodite';
import * as React from 'react';
import { Waypoint } from 'react-waypoint';
import { IPixabayImage } from '../../../models/Pixabay';
import { useErrorMessages } from '../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../models/Logging';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { MediaType } from '../../components/MediaChooser';
import {
	MediaChooserContext,
	useMediaChooserContext,
	useMediaChooserContextController,
} from '../../components/MediaChooser/context';
import { useMediaChooserSearch } from '../../components/MediaChooser/hooks/useMediaChooserSearch';
import { useMediaChooserSuggestions } from '../../components/MediaChooser/hooks/useMediaChooserSuggestions';
import { MediaSuggestionResults } from '../../components/MediaChooser/MediaSuggestionResults';
import { Button } from '../Button';
import { AiButton } from '../AiButton';
import { Input } from '../Input';
import PixabayImageUrl from '../../assets/pixabay_logo.svg';
import { s } from './styles';
import { PixabayImageBrowserProps } from './types';

// Inner component that uses the context
function PixabayImageBrowserContent({
	onImageSelected,
	onEditWithAI,
	content,
}: Pick<PixabayImageBrowserProps, 'onImageSelected' | 'onEditWithAI' | 'content'>) {
	const { selectedImage, setSelectedImage, createFileAttachmentFromSelectedImageAsync, showSuggestions } =
		useMediaChooserContext();
	const { pushApiError } = useErrorMessages();
	const { logApiError } = useEventLogging('PixabayImageBrowser');

	const {
		getMoreImages,
		inputRef,
		isLoading,
		onClearClicked,
		onSearchTermInputChanged,
		onInputKeyDown,
		pendingSearchTerm,
		searchedImages,
		searchTerm,
	} = useMediaChooserSearch();

	const suggestions = useMediaChooserSuggestions({ content });

	const [isUploading, setIsUploading] = React.useState(false);

	const handleImageClick = React.useCallback(
		(image: IPixabayImage) => {
			setSelectedImage(image);
		},
		[setSelectedImage]
	);

	const handleCancel = React.useCallback(() => {
		setSelectedImage(null);
	}, [setSelectedImage]);

	const handleAddImage = React.useCallback(async () => {
		if (!selectedImage) return;

		setIsUploading(true);
		try {
			const attachment = await createFileAttachmentFromSelectedImageAsync();
			if (attachment) {
				onImageSelected(attachment);
			}
		} catch (error) {
			logApiError('PixabayImageBrowser-AddImage-Error', error);
			pushApiError(error);
		} finally {
			setIsUploading(false);
		}
	}, [selectedImage, createFileAttachmentFromSelectedImageAsync, onImageSelected, logApiError, pushApiError]);

	const handleEditWithAI = React.useCallback(async () => {
		if (!selectedImage) return;

		setIsUploading(true);
		try {
			const attachment = await createFileAttachmentFromSelectedImageAsync();
			if (attachment) {
				onEditWithAI(attachment);
			}
		} catch (error) {
			logApiError('PixabayImageBrowser-EditWithAI-Error', error);
			pushApiError(error);
		} finally {
			setIsUploading(false);
		}
	}, [selectedImage, createFileAttachmentFromSelectedImageAsync, onEditWithAI, logApiError, pushApiError]);

	const renderSearch = () => (
		<div className={css(s.searchSection)}>
			<label className={css(s.searchLabel)}>Search for royalty free images</label>
			<div className={css(s.searchRow)}>
				<Input
					autoComplete='off'
					id='pixabay-search-input'
					ref={inputRef}
					onChange={onSearchTermInputChanged}
					onKeyDown={onInputKeyDown}
					placeholder='Search for an image'
					type='text'
					value={pendingSearchTerm || ''}
					styles={[s.searchInput]}
				/>
				{searchedImages?.pages?.length > 0 && (
					<Button variant='primary' onClick={onClearClicked}>
						Clear Search
					</Button>
				)}
			</div>
		</div>
	);

	const renderImageGrid = () => {
		// Only show loading spinner if there's an active search term
		if (isLoading && searchTerm && !searchedImages?.pages?.length) {
			return (
				<div className={css(s.loadingContainer)}>
					<LoadingSpinner />
				</div>
			);
		}

		if (!searchedImages?.pages?.length) {
			return (
				<div className={css(s.emptyState)}>
					<div className={css(s.emptyStateTitle)}>Search for images</div>
					<div className={css(s.emptyStateDescription)}>
						Enter a search term above to find royalty-free images from Pixabay
					</div>
				</div>
			);
		}

		const allImages = searchedImages.pages.flatMap(page => page.hits || []);

		return (
			<>
				<div className={css(s.imageGrid)}>
					{allImages.map(image => (
						<div key={image.id} className={css(s.imageCard)} onClick={() => handleImageClick(image)}>
							<img src={image.previewURL} alt={image.tags} className={css(s.imageCardImage)} />
						</div>
					))}
				</div>
				{!isLoading && searchedImages.pageParams?.length < 10 && (
					<Waypoint onEnter={getMoreImages}>
						<div className={css(s.loadMoreTrigger)}>
							<LoadingSpinner />
						</div>
					</Waypoint>
				)}
			</>
		);
	};

	const renderPreview = () => {
		if (!selectedImage) return null;

		// Check if the selected image is a GIF (animated GIFs are not supported for AI editing)
		const imageUrl = selectedImage.webformatURL || selectedImage.largeImageURL || selectedImage.previewURL || '';
		const isGif = imageUrl.toLowerCase().includes('.gif');

		return (
			<div className={css(s.previewSection)}>
				<div className={css(s.previewImageContainer)}>
					<img
						src={selectedImage.webformatURL || selectedImage.previewURL}
						alt={selectedImage.tags}
						className={css(s.previewImage)}
					/>
				</div>
				<div className={css(s.previewActions)}>
					<Button variant='secondary' onClick={handleCancel} disabled={isUploading}>
						Cancel
					</Button>
					{!isGif && <AiButton title='Edit with AI' onClick={handleEditWithAI} isLoading={isUploading} />}
					<Button variant='primary' onClick={handleAddImage} isLoading={isUploading}>
						Add Image
					</Button>
				</div>
			</div>
		);
	};

	return (
		<div className={css(s.container)}>
			{!selectedImage && renderSearch()}
			<div className={css(s.resultsSection)}>
				{selectedImage ? (
					renderPreview()
				) : (
					<>
						{showSuggestions && content && suggestions.keywords && suggestions.keywords.length > 0 && (
							<MediaSuggestionResults
								keywords={suggestions.keywords}
								onImageClicked={handleImageClick}
								mediaType={MediaType.Image}
							/>
						)}
						{renderImageGrid()}
					</>
				)}
			</div>
			{!selectedImage && (
				<div className={css(s.poweredBy)}>
					<span>Powered by</span>
					<a href='https://pixabay.com/' target='_blank' rel='noreferrer'>
						<img src={PixabayImageUrl} alt='Pixabay' className={css(s.poweredByLogo)} />
					</a>
				</div>
			)}
		</div>
	);
}

// Main component that provides the context
export function PixabayImageBrowser({
	onImageSelected,
	onEditWithAI,
	content,
	imageSearchOptions,
}: PixabayImageBrowserProps) {
	const context = useMediaChooserContextController({ imageSearchOptions });

	return (
		<MediaChooserContext.Provider value={context}>
			<PixabayImageBrowserContent onImageSelected={onImageSelected} onEditWithAI={onEditWithAI} content={content} />
		</MediaChooserContext.Provider>
	);
}
